import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatApiError } from "../../components/AuthContext";
import { usePortalAuth } from "../../components/PortalAuthContext";
import { Loader2, KeyRound } from "lucide-react";

export function PasswordForm({ onSubmit, cta, testid }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("Passwords don't match"); return; }
    setBusy(true); setErr("");
    try { await onSubmit(pw); } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };
  const input = "w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]";
  return (
    <form onSubmit={submit} className="card p-6 space-y-4" data-testid={testid}>
      <div><label className="overline block mb-1.5">New password</label>
        <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className={input} data-testid={`${testid}-password`} /></div>
      <div><label className="overline block mb-1.5">Confirm password</label>
        <input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className={input} data-testid={`${testid}-confirm`} /></div>
      <div className="text-[11px] text-[var(--ink-3)]">At least 8 characters.</div>
      {err && <div className="text-[13px] text-[var(--accent)]" data-testid={`${testid}-error`}>{err}</div>}
      <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid={`${testid}-submit`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} {cta}
      </button>
    </form>
  );
}

export function PortalShell({ title, sub, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--canvas)" }}>
      <div className="w-full max-w-md">
        <div className="overline mb-2">Client portal</div>
        <h1 className="font-heading text-3xl mb-2" style={{ fontWeight: 500 }}>{title}</h1>
        {sub && <p className="text-[var(--ink-2)] mb-8 text-sm">{sub}</p>}
        {children}
      </div>
    </div>
  );
}

export default function PortalReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = usePortalAuth();
  const [check, setCheck] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/portal/auth/reset/${token}`).then((r) => setCheck(r.data)).catch((e) => setCheck({ error: formatApiError(e) }));
  }, [token]);
  if (!check) return <PortalShell title="Reset password"><Loader2 className="animate-spin" /></PortalShell>;
  if (check.error) return (
    <PortalShell title="Link expired" sub={check.error}>
      <Link to="/portal/login?forgot=1" className="btn-primary" data-testid="portal-reset-request-new">Request a new link</Link>
    </PortalShell>
  );
  return (
    <PortalShell title="Choose a new password" sub={check.email ? `For ${check.email}` : null}>
      <PasswordForm testid="portal-reset-form" cta="Save and sign in" onSubmit={async (pw) => {
        await axios.post(`${API_BASE}/portal/auth/reset/${token}`, { password: pw });
        await refresh();
        navigate("/portal");
      }} />
    </PortalShell>
  );
}
